<template>
  <div class="form-page">
    <header class="form-heading">
      <div>
        <p>Mon compte</p>
        <h1>Mon espace</h1>
        <span>Vos informations personnelles.</span>
      </div>
    </header>

    <div class="form-content">
      <div v-if="loading" class="form-panel profile-loading">
        <LoaderDiv
          :isLoading="true"
          variant="modern"
          size="medium"
          loaderText="Chargement de votre profil…"
        />
      </div>

      <form
        v-else
        class="form-panel"
        novalidate
        @submit.prevent="updateProfile"
      >
        <section class="form-section">
          <div class="form-section__heading">
            <b>01</b>
            <div>
              <h2>Identité</h2>
              <p>Votre nom tel qu’il apparaît dans l’application.</p>
            </div>
          </div>
          <div class="form-section__body">
            <label class="form-field">
              Identifiant utilisateur
              <input
                v-model="user.id"
                class="form-input"
                type="text"
                readonly
                placeholder="Généré automatiquement"
              />
            </label>
            <div class="form-row">
              <label class="form-field">
                Prénom
                <input
                  v-model="user.firstname"
                  class="form-input"
                  :class="{ 'is-error': errors.firstname }"
                  type="text"
                  autocomplete="given-name"
                  :disabled="isSubmitting"
                  placeholder="Votre prénom"
                  @blur="validateField('firstname', user.firstname)"
                />
                <span v-if="errors.firstname" class="form-help is-error">{{
                  errors.firstname
                }}</span>
              </label>
              <label class="form-field">
                Nom
                <input
                  v-model="user.lastname"
                  class="form-input"
                  :class="{ 'is-error': errors.lastname }"
                  type="text"
                  autocomplete="family-name"
                  :disabled="isSubmitting"
                  placeholder="Votre nom de famille"
                  @blur="validateField('lastname', user.lastname)"
                />
                <span v-if="errors.lastname" class="form-help is-error">{{
                  errors.lastname
                }}</span>
              </label>
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="form-section__heading">
            <b>02</b>
            <div>
              <h2>Contact</h2>
              <p>
                Les alertes se règlent dans
                <router-link to="/notifications">Notifications</router-link>.
              </p>
            </div>
          </div>
          <div class="form-section__body">
            <label class="form-field">
              Adresse e-mail
              <input
                v-model="user.email"
                class="form-input"
                :class="{ 'is-error': errors.email }"
                type="email"
                autocomplete="email"
                :disabled="isSubmitting"
                placeholder="votre.email@exemple.com"
                @blur="validateField('email', user.email)"
              />
              <span v-if="errors.email" class="form-help is-error">{{
                errors.email
              }}</span>
            </label>
          </div>
        </section>

        <footer class="form-footer">
          <span
            v-if="status.message"
            class="form-footer__status"
            :class="status.error ? 'form-error' : 'form-success'"
            >{{ status.message }}</span
          >
          <button
            type="submit"
            class="form-button form-button--primary"
            :disabled="isSubmitting || !isFormValid"
          >
            {{ isSubmitting ? "Enregistrement…" : "Enregistrer" }}
          </button>
        </footer>
      </form>

      <!-- Sources et achat : où mènent « Consulter » et « Acheter » -->
      <section v-if="links" class="form-panel">
        <div class="form-section">
          <div class="form-section__heading">
            <b>03</b>
            <div>
              <h2>Sources et achat</h2>
              <p>
                Pour chaque site, où mènent « Consulter » et « Acheter » : le
                site d’origine ou un intermédiaire (ZenMarket, Buyee…). Utilisé
                partout : annonces, veille, notifications et extension.
              </p>
            </div>
          </div>
          <div class="form-section__body">
            <div class="links-grid" role="table" aria-label="Liens par source">
              <div class="links-grid__head" role="row">
                <span role="columnheader">Source</span
                ><span role="columnheader">Consulter</span
                ><span role="columnheader">Acheter</span>
              </div>
              <div
                v-for="source in links.sources"
                :key="source.id"
                class="links-grid__row"
                role="row"
              >
                <strong role="rowheader">{{ source.label }}</strong>
                <label role="cell"
                  ><span class="links-grid__label">Consulter</span
                  ><select
                    v-model="links.prefs[source.id].view"
                    class="form-input"
                    :aria-label="`${source.label} : consulter`"
                    :disabled="source.choices.length < 2"
                  >
                    <option
                      v-for="choice in source.choices"
                      :key="choice.id"
                      :value="choice.id"
                    >
                      {{ choice.label }}
                    </option>
                  </select></label
                >
                <label role="cell"
                  ><span class="links-grid__label">Acheter</span
                  ><select
                    v-model="links.prefs[source.id].buy"
                    class="form-input"
                    :aria-label="`${source.label} : acheter`"
                    :disabled="source.choices.length < 2"
                  >
                    <option
                      v-for="choice in source.choices"
                      :key="choice.id"
                      :value="choice.id"
                    >
                      {{ choice.label }}
                    </option>
                  </select></label
                >
              </div>
            </div>
          </div>
        </div>
        <footer class="form-footer">
          <span
            v-if="linksStatus.message"
            class="form-footer__status"
            :class="linksStatus.error ? 'form-error' : 'form-success'"
            >{{ linksStatus.message }}</span
          >
          <button
            type="button"
            class="form-button form-button--secondary"
            :disabled="savingLinks"
            @click="links.prefs = JSON.parse(JSON.stringify(links.defaults))"
          >
            Valeurs par défaut
          </button>
          <button
            type="button"
            class="form-button form-button--primary"
            :disabled="savingLinks"
            @click="saveLinks"
          >
            {{ savingLinks ? "Enregistrement…" : "Enregistrer" }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import config from "@/config/config";
import { isTokenExpired } from "../auth";
import LoaderDiv from "./LoaderDiv.vue";

export default {
  name: "ProfileForm",
  components: {
    LoaderDiv,
  },
  setup() {
    const loading = ref(true);
    const isSubmitting = ref(false);
    const user = ref({ id: "", firstname: "", lastname: "", email: "" });
    const errors = ref({});
    const status = ref({ message: "", error: false });

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const isFormValid = computed(
      () =>
        user.value.firstname &&
        user.value.lastname &&
        user.value.email &&
        isValidEmail(user.value.email) &&
        Object.keys(errors.value).length === 0,
    );

    const showStatus = (message, error = false) => {
      status.value = { message, error };
    };

    const checkAuthToken = () => {
      const token = localStorage.getItem("access_token");
      return Boolean(token && !isTokenExpired(token));
    };

    const validateField = (fieldName, value) => {
      errors.value = { ...errors.value };
      delete errors.value[fieldName];
      if (
        (fieldName === "firstname" || fieldName === "lastname") &&
        (!value || value.trim().length < 2)
      ) {
        errors.value[fieldName] =
          "Ce champ doit contenir au moins 2 caractères";
      }
      if (fieldName === "email" && (!value || !isValidEmail(value))) {
        errors.value[fieldName] = "Veuillez saisir une adresse e-mail valide";
      }
    };

    const loadUserProfile = async () => {
      if (!checkAuthToken()) return;
      try {
        loading.value = true;
        const response = await axios.get(`${config.apiUrl}/user`);
        user.value = { ...user.value, ...response.data };
      } catch (error) {
        console.error("Erreur lors de la récupération du profil:", error);
        showStatus("Impossible de charger votre profil", true);
      } finally {
        loading.value = false;
      }
    };

    // The ntfy channel ("channel") is set on the Notifications page: it is not
    // sent here, the API only updates the fields it receives
    const updateProfile = async () => {
      if (!checkAuthToken()) return;
      validateField("firstname", user.value.firstname);
      validateField("lastname", user.value.lastname);
      validateField("email", user.value.email);
      if (!isFormValid.value) {
        showStatus("Veuillez corriger les erreurs du formulaire", true);
        return;
      }
      try {
        isSubmitting.value = true;
        showStatus("");
        await axios.post(`${config.apiUrl}/user`, {
          email: user.value.email.trim(),
          firstname: user.value.firstname.trim(),
          lastname: user.value.lastname.trim(),
        });
        showStatus("✓ Profil enregistré");
      } catch (error) {
        console.error("Erreur lors de la mise à jour du profil:", error);
        if (error.response?.status === 400) {
          showStatus("Données invalides, vérifiez vos informations", true);
        } else if (error.response?.status === 409) {
          showStatus("Cette adresse e-mail est déjà utilisée", true);
        } else {
          showStatus("Impossible de mettre à jour votre profil", true);
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    // Sources et achat (GET / PUT /user/links)
    const links = ref(null);
    const savingLinks = ref(false);
    const linksStatus = ref({ message: "", error: false });
    const loadLinks = async () => {
      try {
        const { data } = await axios.get(`${config.apiUrl}/user/links`);
        links.value = data;
      } catch (error) {
        console.error("Réglages des liens indisponibles", error);
      }
    };
    const saveLinks = async () => {
      savingLinks.value = true;
      linksStatus.value = { message: "", error: false };
      try {
        const { data } = await axios.put(`${config.apiUrl}/user/links`, {
          prefs: links.value.prefs,
        });
        links.value = data;
        linksStatus.value = { message: "✓ Liens enregistrés", error: false };
      } catch (error) {
        linksStatus.value = {
          message: "Les liens n’ont pas pu être enregistrés",
          error: true,
        };
      } finally {
        savingLinks.value = false;
      }
    };

    onMounted(() => {
      loadUserProfile();
      loadLinks();
    });

    return {
      links,
      linksStatus,
      saveLinks,
      savingLinks,
      loading,
      isSubmitting,
      user,
      errors,
      status,
      isFormValid,
      validateField,
      updateProfile,
    };
  },
};
</script>

<style scoped>
.links-grid {
  display: grid;
  gap: 10px;
}
.links-grid__head,
.links-grid__row {
  display: grid;
  grid-template-columns: minmax(120px, 1.1fr) 1fr 1fr;
  align-items: center;
  gap: 12px;
}
.links-grid__head {
  color: #6f7b8d;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.links-grid__row strong {
  font-size: 13px;
}
.links-grid__row label {
  margin: 0;
}
.links-grid__label {
  display: none;
}
@media (max-width: 640px) {
  .links-grid__head {
    display: none;
  }
  .links-grid__row {
    grid-template-columns: 1fr 1fr;
    padding-bottom: 10px;
    border-bottom: 1px solid #edf0f4;
  }
  .links-grid__row strong {
    grid-column: 1 / -1;
  }
  .links-grid__label {
    display: block;
    margin-bottom: 4px;
    color: #6f7b8d;
    font-size: 11px;
    font-weight: 700;
  }
}
.profile-loading {
  padding: 48px 24px;
}
.form-section__heading a {
  color: #2e6098;
  font-weight: 700;
  text-decoration: none;
}
</style>
